import { NewCashInvestmentsGrid } from "./NewCashInvestmentsGrid";
import React, { useEffect, useState } from "react";
import {
  DealInvestorsFormError,
  DealLegalEntityInvestmentFormEntry,
  getTotalInvestmentAndPercentage,
} from "../DealInvestorDetailPage";
import { primarySeries } from "../../../../api/deals/deals";
import * as api from "../../../../api/generated-api";
import { isEmpty } from "../../../../helpers/utilsTS";
import { simpleSort } from "../../../../helpers/utils";
import {
  createDealLegalEntityInvestment,
  deleteDealLegalEntityInvestment,
} from "../../../../api/deallegalentityinvestment/dealLegalEntityInvestments";
import { updateDealLegalEntityInvestment } from "../../../../api/deals/investors";

type NewCashInvestmentGridWrapperProps = {
  orgId: number;
  dealId: number;
  dealInvestorId: number;
  deal: api.Deal;
  investor: api.DealInvestor;
  canEditInvestorInfo: boolean;
  investmentDealLegalEntities: api.DealLegalEntity[];
  formDataNewCashInvestments: Map<number, DealLegalEntityInvestmentFormEntry>;
  setFormDataNewCashInvestments: (
    formData: Map<number, DealLegalEntityInvestmentFormEntry>
  ) => void;
  fetchDealLegalEntityInvestments: (investorId: number) => void;
  newCashInvestments: api.DealLegalEntityInvestment[];
  setNewCashInvestments: (
    fetchedInvestments: api.DealLegalEntityInvestment[]
  ) => void;
};
export const NewCashInvestmentGridWrapper = (
  props: NewCashInvestmentGridWrapperProps
) => {
  const {
    orgId,
    dealId,
    dealInvestorId,
    deal,
    investor,
    canEditInvestorInfo,
    investmentDealLegalEntities,
    formDataNewCashInvestments,
    setFormDataNewCashInvestments,
    fetchDealLegalEntityInvestments,
    newCashInvestments,
    setNewCashInvestments,
  } = props;

  const [newCashEditMode, setNewCashEditMode] = useState<boolean>(false);
  const [newLegalEntityIdSequence, setNewLegalEntityIdSequence] = useState(0);
  const [errors, setErrors] = useState({});
  const [totalInvestments, setTotalInvestments] = useState(0);
  const [percentage, setPercentage] = useState(0);

  const setAndSortFormDataNewCashInvestments = (
    newCashInvestments: Map<number, DealLegalEntityInvestmentFormEntry>
  ) => {
    setFormDataNewCashInvestments(
      new Map(
        [...newCashInvestments].sort((a, b) => {
          const investmentA = newCashInvestments.get(a[0]);
          const investmentB = newCashInvestments.get(b[0]);
          if (isEmpty(investmentA.name)) {
            return 1;
          } else {
            return simpleSort(investmentA.name, investmentB.name);
          }
        })
      )
    );
  };

  const createNewDealLegalEntityFormEntry =
    (): DealLegalEntityInvestmentFormEntry => {
      setNewLegalEntityIdSequence(newLegalEntityIdSequence - 1);
      return {
        dealSeriesId: primarySeries(deal).id,
        type: api.DealLegalEntityInvestmentType.New_Cash,
        id: newLegalEntityIdSequence,
        targetCashInvestment: 0,
        split: 0,
        exactWireAmount: 0,
        shares: 0,
        name: "",
        signer: undefined,
      };
    };

  const updateErrors = (
    total: number,
    percentage: number
  ): DealInvestorsFormError => {
    let errors: DealInvestorsFormError;
    if (investor.targetInvestment == 0) {
      errors = {
        targetInvestment: "0",
        internalSplit:
          "Cannot create New Cash Investment until Target Investment Amount is greater than 0",
      };
    } else if (total > investor.targetInvestment) {
      errors = {
        targetInvestment: String(total),
        internalSplit:
          percentage + "% Total investment must less or equal 100%",
      };
    } else {
      errors = {};
    }
    return errors;
  };

  const getChangedNewCashInvestments = () => {
    const changes: {
      removed: DealLegalEntityInvestmentFormEntry[];
      updated: DealLegalEntityInvestmentFormEntry[];
      created: DealLegalEntityInvestmentFormEntry[];
    } = {
      removed: [],
      updated: [],
      created: [],
    };

    const existingIds = newCashInvestments.map((dealLegalEntityInvestment) => {
      return dealLegalEntityInvestment.id;
    });

    formDataNewCashInvestments.forEach((formDataEntry, id) => {
      if (existingIds.includes(id)) {
        changes.updated.push(formDataEntry);
      } else {
        changes.created.push(formDataEntry);
      }
    });

    newCashInvestments.forEach((dealLegalEntityInvestment) => {
      if (!formDataNewCashInvestments.has(dealLegalEntityInvestment.id)) {
        changes.removed.push({
          dealSeriesId: primarySeries(deal).id,
          type: dealLegalEntityInvestment.type,
          id: dealLegalEntityInvestment.id,
          targetCashInvestment: dealLegalEntityInvestment.targetCashInvestment,
          split: dealLegalEntityInvestment.split,
          name: dealLegalEntityInvestment.name,
          exactWireAmount: dealLegalEntityInvestment.exactWireAmount,
          shares: dealLegalEntityInvestment.shares,
          signer: dealLegalEntityInvestment.signer,
        });
      }
    });

    return changes;
  };

  const validateNewCashInvestments = () => {
    let totalTargetInvestment = 0;
    const error = {
      targetInvestment: "",
      internalSplit: undefined,
    };
    formDataNewCashInvestments.forEach((le) => {
      if (isEmpty(le.name) && !le.dealInvestorMemberSignerId) {
        error.internalSplit = "Investing Entity Legal Name is required.";
        return;
      }

      if (!le.targetCashInvestment) {
        error.internalSplit = "Target Investment is required.";
        return;
      }

      totalTargetInvestment += +le.targetCashInvestment;
    });

    if (error.internalSplit) {
      setErrors(error);
      return false;
    } else if (investor.targetInvestment < totalTargetInvestment) {
      const percentage = Math.round(
        (totalTargetInvestment / +investor.targetInvestment) * 100
      );
      setErrors({
        targetInvestment: totalTargetInvestment,
        internalSplit:
          percentage + "% Total investment must less or equal 100%",
      });
      return false;
    }
    return true;
  };

  const saveNewNewCashInvestments = (
    legalEntitiesToSave: DealLegalEntityInvestmentFormEntry[]
  ) => {
    if (legalEntitiesToSave?.length) {
      const newDealLegalEntityInvestments = [];
      for (let i = 0; i < legalEntitiesToSave.length; i++) {
        createDealLegalEntityInvestment(
          orgId,
          dealId,
          dealInvestorId,
          {
            dealSeriesId: primarySeries(deal).id,
            type: api.DealLegalEntityInvestmentType.New_Cash,
            name: legalEntitiesToSave[i].name,
            targetCashInvestment: legalEntitiesToSave[i].targetCashInvestment,
            split: legalEntitiesToSave[i].split,
            shares: legalEntitiesToSave[i].shares,
            exactWireAmount: legalEntitiesToSave[i].exactWireAmount,
            dealInvestorMemberSignerId:
              legalEntitiesToSave[i].dealInvestorMemberSignerId,
          },
          (savedInvestment) => {
            newDealLegalEntityInvestments.push(savedInvestment);
          }
        );
      }

      const newFormData = new Map<number, DealLegalEntityInvestmentFormEntry>(
        formDataNewCashInvestments
      );

      legalEntitiesToSave.forEach((formData) => {
        newFormData.delete(formData.id);
      });

      newDealLegalEntityInvestments.forEach((dealLegalEntityInvestment) => {
        newFormData.set(dealLegalEntityInvestment.id, {
          ...dealLegalEntityInvestment,
        });
      });
      setFormDataNewCashInvestments(newFormData);

      setNewCashInvestments(
        newCashInvestments
          .filter((dealLegalEntityInvestment) => {
            if (dealLegalEntityInvestment.id > 0) {
              return dealLegalEntityInvestment;
            }
          })
          .concat(newDealLegalEntityInvestments)
      );
    }
  };

  const updateNewCashInvestments = (
    legalEntitiesToUpdate: DealLegalEntityInvestmentFormEntry[]
  ) => {
    if (legalEntitiesToUpdate?.length) {
      for (let i = 0; i < legalEntitiesToUpdate.length; i++) {
        const legalEntityToUpdate = legalEntitiesToUpdate[i];

        let newName: string;
        if (legalEntityToUpdate.dealInvestorMemberSignerId) {
          newName = investmentDealLegalEntities.find((investmentDLE) => {
            if (
              investmentDLE.id == legalEntityToUpdate.dealInvestorMemberSignerId
            ) {
              return investmentDLE;
            }
          })?.name;
        } else {
          newName = legalEntityToUpdate.name;
        }

        //update local state optimistically
        const preAPIUpdateValues = new Map<
          number,
          DealLegalEntityInvestmentFormEntry
        >(formDataNewCashInvestments);
        preAPIUpdateValues.set(legalEntityToUpdate.id, {
          ...legalEntityToUpdate,
          name: newName,
        });
        setFormDataNewCashInvestments(preAPIUpdateValues);

        updateDealLegalEntityInvestment(
          orgId,
          dealId,
          dealInvestorId,
          legalEntityToUpdate.id,
          {
            name: legalEntityToUpdate.name,
            targetCashInvestment: legalEntityToUpdate.targetCashInvestment,
            split: legalEntityToUpdate.split,
            shares: legalEntityToUpdate.shares,
            exactWireAmount: legalEntityToUpdate.exactWireAmount,
            dealInvestorMemberSignerId:
              legalEntityToUpdate.dealInvestorMemberSignerId,
          },
          (updatedInvestment) => {
            //update local state again with actual server data
            const postAPIUpdateValues = new Map<
              number,
              DealLegalEntityInvestmentFormEntry
            >(formDataNewCashInvestments);
            postAPIUpdateValues.set(legalEntityToUpdate.id, {
              ...updatedInvestment,
            });
            setFormDataNewCashInvestments(postAPIUpdateValues);
          }
        );
      }
    }
  };

  const newCashInvestmentsUpdate = () => {
    const changes = getChangedNewCashInvestments();

    const modifiedFormData = new Map<
      number,
      DealLegalEntityInvestmentFormEntry
    >(formDataNewCashInvestments);

    if (changes.created) {
      changes.created.forEach((formData) => {
        modifiedFormData.set(formData.id, formData);
      });
    }

    if (changes.updated) {
      changes.updated.forEach((formData) => {
        modifiedFormData.set(formData.id, formData);
      });
    }

    if (changes.removed) {
      changes.removed.forEach((formData) => {
        modifiedFormData.delete(formData.id);
      });
    }

    setFormDataNewCashInvestments(modifiedFormData);
    fetchDealLegalEntityInvestments(dealInvestorId);

    saveNewNewCashInvestments(changes.created);
    updateNewCashInvestments(changes.updated);
    removeNewCashInvestments(changes.removed);
  };

  const removeNewCashInvestments = (
    dealLegalEntityFormEntriesRemoved: DealLegalEntityInvestmentFormEntry[]
  ) => {
    dealLegalEntityFormEntriesRemoved.forEach(
      async (dealLegalEntityInvestment) => {
        deleteDealLegalEntityInvestment(
          orgId,
          dealId,
          dealInvestorId,
          dealLegalEntityInvestment.id
        );
      }
    );
  };

  const handleEditForNewCashInvestment = () => {
    setNewCashEditMode(true);
  };

  const handleAddForNewCashInvestment = () => {
    const newDealLegalEntityFormData = createNewDealLegalEntityFormEntry();
    const newFormData = new Map<number, DealLegalEntityInvestmentFormEntry>(
      formDataNewCashInvestments
    );
    newFormData.set(newDealLegalEntityFormData.id, newDealLegalEntityFormData);
    setAndSortFormDataNewCashInvestments(newFormData);
  };

  const handleRemoveForNewCashInvestment = (
    dealLegalEntityInvestmentFormEntry: DealLegalEntityInvestmentFormEntry
  ) => {
    // get the current map value
    const updatedForDataNewCashInvestments = new Map<
      number,
      DealLegalEntityInvestmentFormEntry
    >(formDataNewCashInvestments);

    updatedForDataNewCashInvestments.delete(
      dealLegalEntityInvestmentFormEntry.id
    );

    setFormDataNewCashInvestments(updatedForDataNewCashInvestments);
  };

  const handleSaveForNewCashInvestment = () => {
    const validationResult = validateNewCashInvestments();
    if (validationResult) {
      newCashInvestmentsUpdate();
      setNewCashEditMode(false);
    }
  };

  const handleChangeItemForNewCashInvestment = (
    changedItem: DealLegalEntityInvestmentFormEntry,
    allItems: DealLegalEntityInvestmentFormEntry[]
  ) => {
    const toChangeFormData = new Map<
      number,
      DealLegalEntityInvestmentFormEntry
    >(formDataNewCashInvestments);
    toChangeFormData.set(changedItem.id, changedItem);

    setFormDataNewCashInvestments(toChangeFormData);
    const { total, percentage } = getTotalInvestmentAndPercentage(
      allItems,
      investor.targetInvestment
    );
    const errors = updateErrors(total, percentage);

    setTotalInvestments(total || 0);
    setPercentage(percentage || 0);
    setErrors(errors);
  };

  const handleCancelForNewCashInvestment = () => {
    setNewCashEditMode(false);
    setErrors({});
    fetchDealLegalEntityInvestments(dealInvestorId);
  };

  useEffect(() => {
    if (investor && totalInvestments) {
      if (investor.targetInvestment == 0) {
        setPercentage(100);
      } else {
        setPercentage(
          Math.round((totalInvestments / +investor.targetInvestment) * 100)
        );
      }
    }
  }, [investor, totalInvestments]);

  useEffect(() => {
    const { total, percentage } = getTotalInvestmentAndPercentage(
      Array.from(formDataNewCashInvestments.values()),
      investor.targetInvestment
    );
    const errors = updateErrors(total, percentage);

    setTotalInvestments(total || 0);
    setPercentage(percentage || 0);
    setErrors(errors);
  }, [formDataNewCashInvestments]);

  return (
    <NewCashInvestmentsGrid
      orgId={orgId}
      dealId={dealId}
      dealInvestorId={dealInvestorId}
      editMode={newCashEditMode}
      pricePerShare={primarySeries(deal).pricePerShare}
      items={formDataNewCashInvestments}
      dealLegalEntitiesOfInvestor={investmentDealLegalEntities}
      totalInvestments={totalInvestments}
      totalPercentage={percentage}
      investorTotalInvestments={investor.targetInvestment}
      errors={errors}
      canEditInvestorInfo={canEditInvestorInfo}
      onEdit={handleEditForNewCashInvestment}
      onAdd={handleAddForNewCashInvestment}
      onCancel={handleCancelForNewCashInvestment}
      onSave={handleSaveForNewCashInvestment}
      onRemove={handleRemoveForNewCashInvestment}
      onChangeItem={handleChangeItemForNewCashInvestment}
    />
  );
};
